import { PlaceholderPreviewBase } from "@/components/r3f/utils/placeholder-preview-base";
import { ElementIconType } from "@/components/ui/icons";
import { useScanDate } from "@/components/ui/use-scan-date";
import {
  PREVIEW_CLOSE_DELAY_MS,
  PREVIEW_OPEN_DELAY_MS,
} from "@/data-preparation-tool/ui/scan-preview";
import { useAnnotationIconColor } from "@/hooks/use-annotation-icon-color";
import { useCaptureAiDescription } from "@/hooks/use-capture-ai-description";
import { useCaptureAnnotationItems } from "@/hooks/use-capture-annotation-items";
import { useDelayedDeactivation } from "@/hooks/use-delayed-deactivation";
import { useImgPreviewSignedUrl } from "@/hooks/use-img-preview-signed-url";
import { useCached3DObjectIfReady } from "@/object-cache";
import { isPanoObject } from "@/object-cache-type-guard";
import { changeMode } from "@/store/mode-slice";
import { setFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import { RootState } from "@/store/store";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { selectIElementWorldPosition } from "@faro-lotv/app-component-toolbox";
import { GUID } from "@faro-lotv/foundation";
import {
  IElement,
  IElementImg2d,
  IElementImg360,
  isIElementImg2d,
  isIElementImg360,
} from "@faro-lotv/ielement-types";
import {
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { create } from "zustand";

/** Distance of the preview from the tree, and shift up so its arrow points at the row, in px */
const PREVIEW_DISTANCE = 12;
const PREVIEW_SHIFT_UP = 12;

/** Places the top of the preview next to the row, so it doesn't move when its content grows */
const POPPER_MODIFIERS = [
  {
    name: "offset",
    options: { offset: [-PREVIEW_SHIFT_UP, PREVIEW_DISTANCE] },
  },
];

/** How deep below a tree node its capture image can be (e.g. scan section > time series > 360) */
const MAX_CAPTURE_IMAGE_DEPTH = 3;

function isCaptureImage(el: IElement): el is IElementImg360 | IElementImg2d {
  return isIElementImg360(el) || isIElementImg2d(el);
}

/**
 * @param id The element of a tree node without child nodes
 * @returns the 360 or photo of the capture shown by the node, undefined if the node is not a capture
 */
function selectCaptureImage(id: GUID | undefined) {
  return (state: RootState): IElementImg360 | IElementImg2d | undefined => {
    const element = selectIElement(id)(state);
    if (!element) return;
    if (isCaptureImage(element)) return element;
    return selectChildDepthFirst(
      element,
      isCaptureImage,
      MAX_CAPTURE_IMAGE_DEPTH,
    )(state);
  };
}

/** The tree node whose preview is open, so moving to another capture swaps the preview at once */
const useOpenPreviewStore = create<{
  hoveredId?: GUID;
  openId?: GUID;
}>()(() => ({}));

type CaptureTreePreviewProps = {
  /** The element of the tree node, undefined for nodes without element */
  elementId?: GUID;
  /** The name of the node, shown as title of the preview */
  name: string;
  /** The icon of the node, repeated in the preview */
  icon: ElementIconType;
  /** The row of the tree to anchor the preview to */
  anchorEl: HTMLElement | null;
  /** The visible area of the tree, whose right edge the preview is placed next to */
  treeEl: HTMLElement | null;
  /** Whether the pointer is over the row */
  isNodeHovered: boolean;
  /** True to keep the preview closed, e.g. while the context menu of the row is open */
  isDisabled: boolean;
  /** Called when the preview opens or closes */
  onOpenChange(isOpen: boolean): void;
};

/**
 * @returns the preview of a capture shown next to its tree node on hover: name, date, panorama,
 * AI description, annotations and coordinates. Nodes that are not a capture show nothing.
 */
export function CaptureTreePreview({
  elementId,
  name,
  icon,
  anchorEl,
  treeEl,
  isNodeHovered,
  isDisabled,
  onOpenChange,
}: CaptureTreePreviewProps): JSX.Element | null {
  const image = useAppSelector(selectCaptureImage(elementId));
  const id = image ? elementId : undefined;

  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const isAnotherNodeHovered = useOpenPreviewStore(
    (s) => !!s.hoveredId && s.hoveredId !== id,
  );
  const isWanted =
    useDelayedDeactivation(
      !!id && (isNodeHovered || isPreviewHovered) && !isDisabled,
      PREVIEW_CLOSE_DELAY_MS,
    ) && !isAnotherNodeHovered;

  const isOpen = useOpenAfterDelay(id, isWanted, isNodeHovered);
  useEffect(() => onOpenChange(isOpen), [isOpen, onOpenChange]);

  const panoElement =
    isOpen && image && isIElementImg360(image) ? image : undefined;
  // Load the pano object here, outside the popover, because the popover portal breaks React context.
  // It must not suspend, or the loader of the whole scene shows at each hover: the preview image
  // is shown until the pano is ready
  const panoObject = useCached3DObjectIfReady(
    panoElement,
    "CaptureTreePreview",
  );
  const imageUri = useImgPreviewSignedUrl(isOpen ? image : undefined);
  const createdAt = useScanDate(isOpen ? image : undefined);
  const position = useAppSelector(selectIElementWorldPosition(image?.id));
  const aiDescription = useCaptureAiDescription(isOpen ? image?.id : undefined);

  const dispatch = useAppDispatch();
  const openCapture = useCallback(() => {
    if (!image) return;
    dispatch(setActiveElement(image.id));
    dispatch(setFlyWalkSceneFilter(SceneFilter.Pano));
    dispatch(changeMode("walk"));
  }, [image, dispatch]);
  const { annotations, onAnnotationClick } = useCaptureAnnotationItems(
    isOpen ? image : undefined,
    openCapture,
  );
  const annotationIconColor = useAnnotationIconColor();

  // The rows can be wider than the visible tree, so the preview is placed at the tree's right edge
  const panelEdgeAnchor = useMemo(
    () =>
      anchorEl && {
        getBoundingClientRect: () => {
          const row = anchorEl.getBoundingClientRect();
          const right = Math.min(
            row.right,
            treeEl?.getBoundingClientRect().right ?? row.right,
          );
          return new DOMRect(row.left, row.top, right - row.left, row.height);
        },
      },
    [anchorEl, treeEl],
  );

  if (!isOpen || !image || !panelEdgeAnchor) return null;

  return (
    <PlaceholderPreviewBase
      isCapturePreview
      name={name}
      icon={icon}
      createdAt={createdAt}
      imageUri={imageUri}
      panoElement={panoElement}
      hasPanoramaMedia={
        !(panoObject instanceof Error) && isPanoObject(panoObject)
      }
      aiDescription={aiDescription}
      aiDescriptionImageId={image.id}
      annotations={annotations}
      onAnnotationClick={onAnnotationClick}
      annotationIconColor={annotationIconColor}
      coordinates={position}
      anchorEl={panelEdgeAnchor}
      placement="right-start"
      popperModifiers={POPPER_MODIFIERS}
      disablePortal={false}
      isVisible
      onPreviewHoverChange={setIsPreviewHovered}
    />
  );
}

/**
 * @param id The capture node, undefined if the node is not a capture
 * @param isWanted Whether the preview should be shown
 * @param isNodeHovered Whether the pointer is over the node
 * @returns true once the preview can open: after a short rest on the node, or at once if
 * another capture preview was already open
 */
function useOpenAfterDelay(
  id: GUID | undefined,
  isWanted: boolean,
  isNodeHovered: boolean,
): boolean {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!id || !isNodeHovered) return;
    useOpenPreviewStore.setState({ hoveredId: id });
    return () => {
      if (useOpenPreviewStore.getState().hoveredId === id) {
        useOpenPreviewStore.setState({ hoveredId: undefined });
      }
    };
  }, [id, isNodeHovered]);

  useEffect(() => {
    if (id && isWanted) {
      if (isOpen) return;
      function open(): void {
        setIsOpen(true);
        useOpenPreviewStore.setState({ openId: id });
      }
      if (useOpenPreviewStore.getState().openId) {
        open();
        return;
      }
      const timeout = setTimeout(open, PREVIEW_OPEN_DELAY_MS);
      return () => clearTimeout(timeout);
    }
    if (!isOpen) return;
    setIsOpen(false);
    // Forget the open preview after the close delay, so a capture hovered meanwhile still opens at once
    setTimeout(() => {
      if (useOpenPreviewStore.getState().openId === id) {
        useOpenPreviewStore.setState({ openId: undefined });
      }
    }, PREVIEW_CLOSE_DELAY_MS);
  }, [id, isWanted, isOpen]);

  return isOpen;
}
